import * as React from 'react'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'
import { RichText } from '@owl-nest/rich-text'
import * as hooks from '@owl-nest/hooks'
import * as contentful from '@owl-nest/contentful'

export type AboutTemplateProps = {
  aboutPage: {
    content: contentful.RichText<any>
    lastUpdate: string
    title: string
    slug: string
  }
  locale: string
}

export function AboutTemplate(props: AboutTemplateProps) {
  return (
    <Main>
      <Wrapper>
        <LastPublished>
          {t('Last update')}{' '}
          {new Intl.DateTimeFormat(props.locale, { timeZone: 'UTC' }).format(new Date(props.aboutPage.lastUpdate))}
        </LastPublished>
        <RichText
          document={props.aboutPage.content.json}
          links={props.aboutPage.content.links}
          renderNode={{
            Title1,
            Title2,
            Title3,
            Title4,
            Paragraph,
            BlockQuote: plume.styles.wysiwyg.BlockQuote,
            Hyperlink: ({ children, uri }) => <Hyperlink href={uri}>{children}</Hyperlink>,
            ListItem,
            UnorderedList: plume.styles.wysiwyg.UnorderedList,
            OrderedList: plume.styles.wysiwyg.OrderedList,
            Table,
            TableCell,
            TableHeaderCell,
            TableRow,
          }}
          renderMark={{
            Bold: plume.styles.wysiwyg.Bold,
            Italic: plume.styles.wysiwyg.Italic,
            Underline: plume.styles.wysiwyg.Underline,
          }}
        />
      </Wrapper>
    </Main>
  )
}

const LastPublished = styled(plume.styles.copy.XS)`
  color: ${plume.COLORS.GREY_SHADE_2};
  margin-bottom: 32px;
`

function Table(props: { children: React.ReactNode }) {
  const isClientSide = hooks.useClientSide()

  if (props.children && Array.isArray(props.children)) {
    const tableHead = props.children[0]
    const restRows = props.children.slice(1)

    return (
      <TableContainer>
        {restRows.map((node, index) => {
          if (isClientSide && !plume.utils.isMobile()) {
            return (
              <RowContainer key={node.key} headLength={tableHead.props.children.length}>
                {index === 0 && tableHead}
                {node}
              </RowContainer>
            )
          }
          return (
            <RowContainer key={node.key}>
              {tableHead}
              {node}
            </RowContainer>
          )
        })}
      </TableContainer>
    )
  } else {
    return <></>
  }
}

type RowContainerProps = {
  className?: string
  children: React.ReactNode
}

function RowContainerComponent({ children, className }: RowContainerProps) {
  const divRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const isMobile = plume.utils.isMobile()

    if (isMobile) {
      const div = divRef.current
      if (div === null) {
        return
      }

      const cells = div.children[1].children
      const heads = div.children[0].children

      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i] as HTMLElement
        const head = heads[i] as HTMLElement

        const cellBoundingClientRect = cell.getBoundingClientRect()
        if (cellBoundingClientRect.height > 20) {
          head.style.height = `${cellBoundingClientRect.height}px`
        } else {
          const headBoundingClientRect = head.getBoundingClientRect()
          cell.style.height = `${headBoundingClientRect.height}px`
        }
      }
    }
  }, [])

  return (
    <div ref={divRef} className={className}>
      {children}
    </div>
  )
}

const Paragraph = styled(plume.styles.wysiwyg.Paragraph)`
  margin: 11px 0;
`

const TableContainer = styled.div`
  border-top: 1px solid ${plume.COLORS.GREY_SHADE_5};

  ${Paragraph} {
    margin: 0;
  }

  a {
    word-break: break-all;
  }
`

const TableRow = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-auto-flow: row;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    grid-auto-flow: column;
  }
`

const RowContainer = styled(RowContainerComponent)<{ headLength?: number }>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 50% 50%;
  border-bottom: 1px solid ${plume.COLORS.GREY_SHADE_5};

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    grid-auto-flow: row;
    grid-template-columns: initial;

    ${TableRow} {
      ${({ headLength }) => {
        if (headLength) {
          return css`
            grid-template-columns: ${Array.from({ length: headLength + 1 }).join('1fr ')};
          `
        }
      }}
    }
  }
`

const TableCell = styled.div`
  > p {
    padding: 8px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    > p {
      height: auto;
      width: auto;
    }
  }
`

const TableHeaderCell = styled.div`
  background-color: ${plume.COLORS.GREY_SHADE_6};

  > p {
    font-weight: 600;
    padding: 8px;
  }
`

const ListItem = styled(plume.styles.copy.S).attrs(() => ({ as: 'li' }))`
  p {
    display: initial;
  }
`

const Hyperlink = styled(plume.Link).attrs(() => ({ tinted: true }))``

const Title1 = styled(plume.styles.wysiwyg.Title)`
  ${plume.styles.heading._xLargeStyle}
`

const Title2 = styled(plume.styles.wysiwyg.Title2)`
  ${plume.styles.heading._largeStyle}
`

const Title3 = styled(plume.styles.wysiwyg.Title3)`
  ${plume.styles.heading._mediumStyle}
`

const Title4 = styled(plume.styles.wysiwyg.Title4)`
  ${plume.styles.heading._xSmallStyle}
`

const Wrapper = styled.div`
  background-color: ${plume.COLORS.PRIMARY_WHITE};
  border-radius: 8px;
  margin: 0 auto;
  padding: 24px;
  max-width: 806px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 32px;
  }
`

const Main = styled.main`
  background-color: ${plume.COLORS.PRIMARY_SAND};

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 88px 0;
  }
`
